import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Trophy } from 'lucide-react';
import { dailyCheckIns } from '../utils/api';

const periodOptions = [
  ['last7', 'Last 7 Days'],
  ['last30', 'Last 30 Days'],
  ['lastWeek', 'Last Week'],
  ['lastMonth', 'Last Month'],
  ['custom', 'Custom dates'],
];

const percent = (value) => (value == null ? '—' : `${Math.round(value * 100)}%`);
const decimal = (value) => Number(value || 0).toFixed(1);

const DailyNumbersLeaderboard = ({
  audience = 'coach',
  variant = 'default',
  refreshKey = 0,
  embedded = false,
}) => {
  const [period, setPeriod] = useState('last30');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadLeaderboard = useCallback(async (overrides = {}) => {
    const requestedPeriod = overrides.period || period;
    if (requestedPeriod === 'custom' && !(customStart && customEnd)) return;
    setLoading(true);
    setError('');
    try {
      const params = {
        period: requestedPeriod,
        ...(requestedPeriod === 'custom' ? { startDate: customStart, endDate: customEnd } : {}),
      };
      const response = audience === 'agent'
        ? await dailyCheckIns.agent.getLeaderboard(params)
        : await dailyCheckIns.getLeaderboard(params);
      setLeaderboard(response.data.data);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Could not load the daily-numbers leaderboard.');
    } finally {
      setLoading(false);
    }
  }, [audience, customEnd, customStart, period]);

  useEffect(() => {
    loadLeaderboard();
  }, [audience, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const changePeriod = (value) => {
    setPeriod(value);
    if (value !== 'custom') loadLeaderboard({ period: value });
  };

  const isPresentation = variant === 'presentation';
  const selectedPeriodLabel = period === 'custom' && customStart && customEnd
    ? `${customStart} to ${customEnd}`
    : periodOptions.find(([value]) => value === period)?.[1] || 'Selected period';

  return (
    <section
      id={`daily-leaderboard-${audience}-${variant}`}
      className={isPresentation
        ? 'overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 text-white shadow-2xl'
        : embedded
          ? 'min-w-0 overflow-hidden'
          : 'mb-8 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm'}
    >
      <header
        id={`daily-leaderboard-header-${audience}-${variant}`}
        className={`flex flex-col gap-4 border-b px-5 py-4 lg:flex-row lg:items-end ${
          isPresentation ? 'border-slate-700' : 'border-gray-200'
        } ${embedded ? 'lg:justify-end' : 'lg:justify-between'}`}
      >
        {!embedded && (
          <section id={`daily-leaderboard-heading-${audience}-${variant}`}>
            <p className={`text-sm font-semibold uppercase tracking-[0.16em] ${isPresentation ? 'text-blue-300' : 'text-blue-600'}`}>Production activity</p>
            <h2 className={`mt-1 flex items-center gap-2 font-bold ${isPresentation ? 'text-3xl text-white' : 'text-xl text-gray-900'}`}><Trophy className={`${isPresentation ? 'h-8 w-8' : 'h-5 w-5'} text-amber-400`} /> Daily Numbers Leaderboard</h2>
            <p className={`mt-1 ${isPresentation ? 'text-lg text-slate-300' : 'text-sm text-gray-500'}`}>Ranked by conversations, then appointments. Attendance stays separate.</p>
          </section>
        )}
        <section id={`daily-leaderboard-controls-${audience}-${variant}`} className="flex flex-wrap items-end gap-2">
          <label htmlFor={`daily-leaderboard-period-${audience}-${variant}`} className={`text-xs font-semibold uppercase tracking-wide ${isPresentation ? 'text-slate-300' : 'text-gray-500'}`}>
            Period
            <select id={`daily-leaderboard-period-${audience}-${variant}`} value={period} onChange={(event) => changePeriod(event.target.value)} className="mt-1 block min-h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-800">
              {periodOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          {period === 'custom' && (
            <>
              <label htmlFor={`daily-leaderboard-start-${audience}-${variant}`} className={`text-xs font-semibold uppercase tracking-wide ${isPresentation ? 'text-slate-300' : 'text-gray-500'}`}>From<input id={`daily-leaderboard-start-${audience}-${variant}`} type="date" value={customStart} onChange={(event) => setCustomStart(event.target.value)} className="mt-1 block min-h-11 rounded-lg border border-gray-300 px-2 text-sm text-gray-800" /></label>
              <label htmlFor={`daily-leaderboard-end-${audience}-${variant}`} className={`text-xs font-semibold uppercase tracking-wide ${isPresentation ? 'text-slate-300' : 'text-gray-500'}`}>To<input id={`daily-leaderboard-end-${audience}-${variant}`} type="date" value={customEnd} onChange={(event) => setCustomEnd(event.target.value)} className="mt-1 block min-h-11 rounded-lg border border-gray-300 px-2 text-sm text-gray-800" /></label>
            </>
          )}
          <button id={`daily-leaderboard-refresh-${audience}-${variant}`} type="button" onClick={() => loadLeaderboard()} disabled={loading || (period === 'custom' && !(customStart && customEnd))} className={`inline-flex min-h-11 items-center rounded-lg border px-3 text-sm font-semibold disabled:opacity-50 ${isPresentation ? 'border-slate-600 text-white hover:bg-slate-800' : 'border-gray-300 text-gray-700'}`}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> {period === 'custom' ? 'Apply' : 'Refresh'}
          </button>
        </section>
      </header>

      {leaderboard?.currentAgent && (
        <aside id={`daily-leaderboard-current-agent-${audience}-${variant}`} className="border-b border-blue-100 bg-blue-50 px-5 py-4 text-blue-950">
          <p className="text-sm font-bold uppercase tracking-wide text-blue-700">Your numbers · {selectedPeriodLabel}</p>
          <dl className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div id={`current-agent-rank-${audience}-${variant}`}>
              <dt className="text-blue-700">Rank</dt>
              <dd className="text-xl font-black">#{leaderboard.currentAgent.rank}</dd>
            </div>
            <div id={`current-agent-production-${audience}-${variant}`}>
              <dt className="text-blue-700">Conversations / appointments</dt>
              <dd className="text-xl font-black">{leaderboard.currentAgent.totalConversations} / {leaderboard.currentAgent.appointmentsSet}</dd>
            </div>
            <div id={`current-agent-reporting-${audience}-${variant}`}>
              <dt className="text-blue-700">Days reported</dt>
              <dd className="text-xl font-black">{leaderboard.currentAgent.reportingDays ?? 0} / {leaderboard.currentAgent.reportingDaysExpected ?? 0}</dd>
            </div>
            <div id={`current-agent-attendance-${audience}-${variant}`}>
              <dt className="text-blue-700">Power Ups attended</dt>
              <dd className="text-xl font-black">{leaderboard.currentAgent.powerUpsAttended ?? 0} / {leaderboard.currentAgent.powerUpsScheduled ?? 0}</dd>
            </div>
          </dl>
        </aside>
      )}
      {loading && <p id={`daily-leaderboard-loading-${audience}-${variant}`} role="status" className={`px-5 py-4 text-sm font-medium ${isPresentation ? 'text-slate-300' : 'text-gray-600'}`}>Loading daily numbers…</p>}
      {error && <p id={`daily-leaderboard-error-${audience}-${variant}`} className={`px-5 py-4 text-sm font-medium ${isPresentation ? 'text-rose-300' : 'text-rose-700'}`}>{error}</p>}

      <section id={`daily-leaderboard-scroll-${audience}-${variant}`} className={isPresentation ? 'overflow-x-auto' : ''}>
        {isPresentation ? (
          <table id={`daily-leaderboard-table-${audience}-${variant}`} className="w-full min-w-[760px] text-left">
            <thead className="bg-slate-900 text-sm font-bold uppercase tracking-wide text-slate-400">
              <tr><th className="px-6 py-4">Rank</th><th className="px-6 py-4">Agent</th><th className="px-6 py-4 text-right">Conversations</th><th className="px-6 py-4 text-right">Appointments</th><th className="px-6 py-4 text-right">Reporting</th><th className="px-6 py-4 text-right">Power Ups</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {(leaderboard?.rows || []).slice(0, 12).map((row) => (
                <tr key={row.agentId} className="bg-slate-950">
                  <td className="px-6 py-4 text-2xl font-black text-amber-300">#{row.rank}</td>
                  <td className="px-6 py-4 text-xl font-bold text-white">{row.agent}</td>
                  <td className="px-6 py-4 text-right text-2xl font-black text-blue-300">{row.totalConversations}</td>
                  <td className="px-6 py-4 text-right text-xl font-bold text-white">{row.appointmentsSet}</td>
                  <td className="px-6 py-4 text-right text-lg text-slate-200">{row.reportingDays}/{row.reportingDaysExpected} · {percent(row.reportingCompletionRate)}</td>
                  <td className="px-6 py-4 text-right text-lg text-slate-200">{row.powerUpsAttended}/{row.powerUpsScheduled}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <>
            <section id={`daily-leaderboard-mobile-${audience}-${variant}`} className="divide-y divide-gray-100 md:hidden">
              {(leaderboard?.rows || []).map((row) => (
                <article id={`daily-leaderboard-mobile-row-${audience}-${row.agentId}`} key={row.agentId} className={row.isCurrentAgent ? 'bg-blue-50 px-4 py-4' : 'bg-white px-4 py-4'}>
                  <header id={`daily-leaderboard-mobile-heading-${audience}-${row.agentId}`} className="flex items-center justify-between gap-3">
                    <h3 className="min-w-0 truncate font-bold text-gray-950">{row.agent}{row.isCurrentAgent ? <span className="ml-2 text-xs font-bold uppercase text-blue-700">You</span> : null}</h3>
                    <span className="text-lg font-black text-amber-600">#{row.rank}</span>
                  </header>
                  <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    <div id={`mobile-production-${audience}-${row.agentId}`}>
                      <dt className="text-gray-500">Conversations / appointments</dt>
                      <dd className="text-lg font-black text-gray-950">{row.totalConversations} / {row.appointmentsSet}</dd>
                    </div>
                    <div id={`mobile-reporting-${audience}-${row.agentId}`}>
                      <dt className="text-gray-500">Days reported</dt>
                      <dd className="font-bold text-gray-900">{row.reportingDays} / {row.reportingDaysExpected} · {percent(row.reportingCompletionRate)}</dd>
                    </div>
                    <div id={`mobile-attendance-${audience}-${row.agentId}`}>
                      <dt className="text-gray-500">Power Ups attended</dt>
                      <dd className="font-bold text-gray-900">{row.powerUpsAttended} / {row.powerUpsScheduled} · {percent(row.attendanceRate)}</dd>
                    </div>
                    <div id={`mobile-average-${audience}-${row.agentId}`}>
                      <dt className="text-gray-500">Avg conversations / report</dt>
                      <dd className="font-bold text-gray-900">{decimal(row.averageConversationsPerReportingDay)}</dd>
                    </div>
                  </dl>
                </article>
              ))}
            </section>
            <section id={`daily-leaderboard-desktop-${audience}-${variant}`} className="hidden overflow-x-auto md:block">
              <table id={`daily-leaderboard-table-${audience}-${variant}`} className="min-w-[1120px] w-full text-left text-sm">
                <thead className="bg-gray-50 text-xs font-bold uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3">Rank</th><th className="px-4 py-3">Agent</th><th className="px-4 py-3 text-right">Conversations</th><th className="px-4 py-3 text-right">Appointments</th><th className="px-4 py-3 text-right">Reporting</th><th className="px-4 py-3 text-right">Avg / report</th><th className="px-4 py-3 text-right">Avg / period day</th><th className="px-4 py-3 text-right">Conversion</th><th className="px-4 py-3 text-right">Org share</th><th className="px-4 py-3 text-right">Power Ups</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(leaderboard?.rows || []).map((row) => (
                    <tr key={row.agentId} className={row.isCurrentAgent ? 'bg-blue-50' : 'bg-white'}>
                      <td className="px-4 py-3 font-bold text-gray-700">#{row.rank}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{row.agent}{row.isCurrentAgent ? ' (You)' : ''}</td>
                      <td className="px-4 py-3 text-right font-bold">{row.totalConversations}</td>
                      <td className="px-4 py-3 text-right">{row.appointmentsSet}</td>
                      <td className="px-4 py-3 text-right">{row.reportingDays}/{row.reportingDaysExpected} · {percent(row.reportingCompletionRate)}</td>
                      <td className="px-4 py-3 text-right">{decimal(row.averageConversationsPerReportingDay)}</td>
                      <td className="px-4 py-3 text-right">{decimal(row.averageConversationsAcrossPeriod)}</td>
                      <td className="px-4 py-3 text-right">{percent(row.conversationToAppointmentRate)}</td>
                      <td className="px-4 py-3 text-right">{percent(row.organizationConversationPercentage)}</td>
                      <td className="px-4 py-3 text-right">{row.powerUpsAttended}/{row.powerUpsScheduled} · {percent(row.attendanceRate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </>
        )}
      </section>
      {!loading && leaderboard?.rows?.length === 0 && <p id={`daily-leaderboard-empty-${audience}-${variant}`} className={`px-5 py-8 text-center ${isPresentation ? 'text-slate-300' : 'text-gray-500'}`}>No active agents are available for this period.</p>}
    </section>
  );
};

export default DailyNumbersLeaderboard;
